// UserLevelBadge.jsx
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { color } from '../style/variables.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { USER_LEVEL_BADGE_PREFIX } from '../RemoteConfigKeys.js';

export const UserLevelBadgeVersion = {
  simple: 'simple',
  complex: 'complex',
};

export const UserLevelBadge = ({
  size = null,
  version = UserLevelBadgeVersion.simple,
  level = 0,
  maxLevel = null,
  vipBenefitLevel = 0,
  margin = '',
  shouldDirectToInformation = false,
  shouldShowLevelZero = false,
  publicBadgeUrlPath = '',
  directToInfomation = NULL_FUNCTION,
  showPointer = false,
}) => {
  const userLevelBadgePrefix = useSelector(state =>
    getRemoteConfigData(state, USER_LEVEL_BADGE_PREFIX)
  );

  const isMaxLevel = level === maxLevel;
  const handleBadgeClick = useCallback(() => {
    directToInfomation();
  }, [directToInfomation]);

  if (typeof vipBenefitLevel !== 'number' || vipBenefitLevel < 0) return null;
  if (!shouldShowLevelZero && vipBenefitLevel <= 0) return null;
  const fileType = version === UserLevelBadgeVersion.complex ? 'png' : 'svg';
  const imgSrc = getResourceUrl({
    path: publicBadgeUrlPath,
    // In remote config, the highest level in USER_LEVEL_BENEFIT_THRESHOLD
    // has its own dedicated badge icon.
    endpoint: `/${userLevelBadgePrefix}_badge_${version}_${isMaxLevel ? 'max' : vipBenefitLevel}.${fileType}`,
    resourceType: PUBLIC,
  }).href;
  const isSimpleVersion = version === UserLevelBadgeVersion.simple;

  const getSimpleBadgeWidth = () => {
    if (isMaxLevel) return 53;
    if (vipBenefitLevel === 0) return 39;
    if (vipBenefitLevel === 1) return 35;
    if (vipBenefitLevel === 2) return 43;
    if (vipBenefitLevel >= 3 && vipBenefitLevel <= 4) return 45;
    return 46;
  };

  return (
    <StyledUserLevelBadge isLink={shouldDirectToInformation || showPointer}>
      <DecryptionWrapper resourceUrl={imgSrc}>
        {isSimpleVersion ? (
          <SimpleBadge
            $width={getSimpleBadgeWidth()}
            $level={level}
            $margin={margin}
            $imgSrc={imgSrc}
            $vipBenefitLevel={vipBenefitLevel}
            {...(shouldDirectToInformation
              ? {
                  onClick: handleBadgeClick,
                }
              : {})}
          >
            {level === 0 ? `LV${level}` : level}
          </SimpleBadge>
        ) : (
          <ComplexBadge
            src={imgSrc}
            alt={`Level ${vipBenefitLevel}`}
            $size={size}
            $margin={margin}
            $imgSrc={imgSrc}
            {...(shouldDirectToInformation
              ? {
                  onClick: handleBadgeClick,
                }
              : {})}
          />
        )}
      </DecryptionWrapper>
    </StyledUserLevelBadge>
  );
};

UserLevelBadge.propTypes = {
  size: PropTypes.number,
  margin: PropTypes.string,
  level: PropTypes.number,
  shouldDirectToInformation: PropTypes.bool,
  shouldShowLevelZero: PropTypes.bool,
  showPointer: PropTypes.bool,
  version: PropTypes.oneOf(Object.values(UserLevelBadgeVersion)),
  publicBadgeUrlPath: PropTypes.string,
  directToInfomation: PropTypes.func,
  vipBenefitLevel: PropTypes.number,
  maxLevel: PropTypes.number,
};

const StyledUserLevelBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  cursor: ${({ isLink }) => (isLink ? 'pointer' : 'auto')};
`;

const ComplexBadge = styled.img`
  height: ${({ $size }) => ($size ? `${$size}px` : 'auto')};
  width: ${({ $size }) => ($size ? `${$size}px` : 'auto')};
  margin: ${({ $margin }) => $margin};
`;

const SimpleBadge = styled.div`
  height: 20px;
  width: ${({ $width }) => `${$width}px`};
  margin: ${({ $margin }) => $margin};
  padding-right: ${({ $level }) => ($level === 0 ? '0px' : '8px')};
  text-align: ${({ $level }) => ($level === 0 ? 'center' : 'right')};
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  background-image: url(${({ $imgSrc }) => $imgSrc});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ $vipBenefitLevel }) =>
    $vipBenefitLevel === 0 ? '#c8c8c8' : color.white};
`;

export default UserLevelBadge;
